'use client';

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Link from 'next/link';
import { useParams, useSearchParams, useRouter } from 'next/navigation';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../../components/Footer';
import useCheckLogin from '../../../../components/CheckLogin';
import ExpertHelpCard from '../../../../components/ExpertHelpCard';
import AdBanner from '../../../../components/AdBanner';
import MobileListingTopBar from '../../../../components/MobileListingTopBar';
import InterestedPopup from '../../../../components/InterestedPopup';
import CivilCategoryHeader from '@/app/components/CivilCategoryHeader';
import CivilSupplierCard from '@/app/components/CivilSupplierCard';
import { api } from '@/lib/apiClient';

const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'views', label: 'Most Viewed' },
    { value: 'experience', label: 'Most Experienced' },
    { value: 'rating', label: 'Top Rated' },
    { value: 'newest', label: 'Newest First' },
];

function convertToArray(str) {
    if (!str) return [];
    return str.split('-').map(num => parseInt(num, 10)).filter(n => !isNaN(n));
}

export default function CivilServicesCityPage() {
    const routeParams = useParams();
    const searchParams = useSearchParams();
    const router = useRouter();
    const isLoggedIn = useCheckLogin();

    const citySlug = routeParams?.city || 'all';
    const cityIdParam = searchParams.get('cityId');
    const localitiesParam = searchParams.get('localities');
    const initialMainCatParam = searchParams.get('category') || 'all';
    const initialSubCatParam = searchParams.get('subCategory') || 'all';

    // Categories and Suppliers state
    const [mainCategories, setMainCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);

    // Progressive loading pagination
    const [visibleCount, setVisibleCount] = useState(3);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const observerRef = useRef(null);
    const sentinelRef = useRef(null);

    // Selection & Filter states
    const [selectedMainCatId, setSelectedMainCatId] = useState(initialMainCatParam);
    const [selectedSubCatId, setSelectedSubCatId] = useState(initialSubCatParam);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [sortBy, setSortBy] = useState('relevance');
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    const [selectedExp, setSelectedExp] = useState('all'); // 'all', '1-3', '3-5', '5+'
    const [filters, setFilters] = useState({});
    const [isFilterLoading, setIsFilterLoading] = useState(false);
    const filterTimeoutRef = useRef(null);

    const triggerFilterTransition = useCallback(() => {
        if (filterTimeoutRef.current) {
            clearTimeout(filterTimeoutRef.current);
        }
        setIsFilterLoading(true);
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        filterTimeoutRef.current = setTimeout(() => {
            setIsFilterLoading(false);
        }, 350);
    }, []);

    useEffect(() => {
        return () => {
            if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
        };
    }, []);

    // UI & Navigation states
    const [cityName, setCityName] = useState('');
    const [localitiesNames, setLocalitiesNames] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeSupplierForContact, setActiveSupplierForContact] = useState(null);
    const [showInterestedPopup, setShowInterestedPopup] = useState(false);

    // Initial Data Fetch
    useEffect(() => {
        fetchInitialData();
    }, [citySlug, cityIdParam, localitiesParam]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            setError(null);

            // 1. Fetch Main Categories (master_main_categories)
            const mainCatPromise = api.post('selectwithout/master_main_categories', {
                sqlData: 'id, category_name, category_img, is_active',
                where: 'is_active=$1 ORDER BY created_at ASC',
                whereData: [true],
            }, { skipAuthGuard: true });

            // 2. Fetch Sub Categories (master_product_categories)
            const subCatPromise = api.post('selectwithout/master_product_categories', {
                sqlData: 'id, product_category_name, description, category_img, main_category_id, is_active',
                where: 'is_active=$1 ORDER BY product_category_name ASC',
                whereData: [true],
            }, { skipAuthGuard: true });

            // 3. Resolve City Details
            let detectedCityName = '';
            if (cityIdParam) {
                try {
                    const cityRes = await api.post('selectwithout/view_home_cities', {
                        sqlData: '*',
                        where: 'id=$1',
                        whereData: [cityIdParam],
                    }, { skipAuthGuard: true });
                    if (cityRes.success === 1 && cityRes.data?.length > 0) {
                        detectedCityName = cityRes.data[0].name;
                    }
                } catch (e) {
                    console.warn('Could not fetch city info:', e);
                }
            } else if (citySlug && citySlug !== 'all') {
                detectedCityName = citySlug.charAt(0).toUpperCase() + citySlug.slice(1);
            }
            setCityName(detectedCityName);

            // 4. Resolve Localities
            const localitiesArray = convertToArray(localitiesParam);
            if (localitiesArray.length > 0) {
                try {
                    const locRes = await api.post('selectwithout/master_localities', {
                        sqlData: 'locality_name as name',
                        where: 'id = ANY($1)',
                        whereData: [localitiesArray],
                    }, { skipAuthGuard: true });
                    if (locRes.success === 1 && Array.isArray(locRes.data)) {
                        setLocalitiesNames(locRes.data.map(l => l.name).join(', '));
                    }
                } catch (e) {
                    console.warn('Could not fetch locality info:', e);
                }
            } else {
                setLocalitiesNames('');
            }

            // 5. Build Suppliers Query
            let whereClause = '1=1';
            const params = [];

            if (cityIdParam) {
                params.push(cityIdParam);
                whereClause += ` AND city_id = $${params.length}`;
            }

            if (localitiesArray.length > 0) {
                params.push(localitiesArray);
                whereClause += ` AND locality_id = ANY($${params.length})`;
            }

            whereClause += ' ORDER BY created_at DESC';

            const suppliersPromise = api.post('selectwithout/view_admin_civil_suppliers', {
                sqlData: '*, (SELECT supplier_logo FROM suppliers WHERE suppliers.id = view_admin_civil_suppliers.id) AS supplier_logo, (SELECT category_ids FROM suppliers WHERE suppliers.id = view_admin_civil_suppliers.id) AS category_ids',
                where: whereClause,
                whereData: params,
            }, { skipAuthGuard: true });

            // Await all concurrently
            const [mainCatRes, subCatRes, supRes] = await Promise.all([
                mainCatPromise,
                subCatPromise,
                suppliersPromise,
            ]);

            if (mainCatRes.success === 1 && Array.isArray(mainCatRes.data)) {
                setMainCategories(mainCatRes.data);
            }

            if (subCatRes.success === 1 && Array.isArray(subCatRes.data)) {
                setSubCategories(subCatRes.data);
            }

            if (supRes.success === 1 && Array.isArray(supRes.data)) {
                const formatted = supRes.data.map(s => {
                    let catIds = [];
                    if (Array.isArray(s.category_ids)) {
                        catIds = s.category_ids;
                    } else if (typeof s.category_ids === 'string') {
                        try { catIds = JSON.parse(s.category_ids); } catch (e) { }
                    }
                    return {
                        ...s,
                        category_ids: catIds,
                        supplier_name: s.name || s.supplier_name,
                        views_count: parseInt(s.views, 10) || parseInt(s.views_count, 10) || 0,
                    };
                });
                setSuppliers(formatted);
            }
        } catch (err) {
            console.error('Error fetching civil page data:', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    // Fast lookup map of subcategories
    const categoryMap = useMemo(() => {
        const map = {};
        subCategories.forEach(sc => {
            map[sc.id] = {
                name: sc.product_category_name,
                description: sc.description,
                image: sc.category_img,
                mainCatId: sc.main_category_id,
            };
        });
        return map;
    }, [subCategories]);

    // Compute live supplier counts per subcategory
    const subCategoryCounts = useMemo(() => {
        const counts = {};
        subCategories.forEach(sc => { counts[sc.id] = 0; });
        suppliers.forEach(s => {
            if (Array.isArray(s.category_ids)) {
                s.category_ids.forEach(catId => {
                    if (counts[catId] !== undefined) {
                        counts[catId]++;
                    }
                });
            }
        });
        return counts;
    }, [subCategories, suppliers]);

    // Compute live supplier counts per main category
    const mainCategoryCounts = useMemo(() => {
        const counts = {};
        mainCategories.forEach(mc => { counts[mc.id] = 0; });
        suppliers.forEach(s => {
            if (Array.isArray(s.category_ids)) {
                const mainCatsMatched = new Set();
                s.category_ids.forEach(catId => {
                    const subCat = categoryMap[catId];
                    if (subCat && subCat.mainCatId) {
                        mainCatsMatched.add(subCat.mainCatId);
                    }
                });
                mainCatsMatched.forEach(mainId => {
                    if (counts[mainId] !== undefined) {
                        counts[mainId]++;
                    }
                });
            }
        });
        return counts;
    }, [mainCategories, suppliers, categoryMap]);

    // Handle Main Category Change
    const handleSelectMainCategory = (mainCatId) => {
        setSelectedMainCatId(mainCatId);
        setSelectedSubCatId('all');
        triggerFilterTransition();

        // Optional: Update URL query param smoothly
        const params = new URLSearchParams(searchParams.toString());
        if (mainCatId !== 'all') {
            params.set('category', mainCatId);
        } else {
            params.delete('category');
        }
        params.delete('subCategory');
        router.replace(`/civil/${citySlug}?${params.toString()}`, { scroll: false });
    };

    // Handle Sub Category Change
    const handleSelectSubCategory = (subCatId) => {
        setSelectedSubCatId(subCatId);
        triggerFilterTransition();

        const params = new URLSearchParams(searchParams.toString());
        if (subCatId !== 'all') {
            params.set('subCategory', subCatId);
        } else {
            params.delete('subCategory');
        }
        router.replace(`/civil/${citySlug}?${params.toString()}`, { scroll: false });
    };

    // Filter and Sort Suppliers
    const filteredSuppliers = useMemo(() => {
        let list = [...suppliers];

        // 1. Filter by Subcategory if selected
        if (selectedSubCatId !== 'all') {
            list = list.filter(s => Array.isArray(s.category_ids) && s.category_ids.includes(selectedSubCatId));
        } else if (selectedMainCatId !== 'all') {
            // Filter by Main Category (suppliers with at least one subcategory under this main category)
            const subCatIdsInMain = subCategories
                .filter(sc => sc.main_category_id === selectedMainCatId)
                .map(sc => sc.id);

            list = list.filter(s =>
                Array.isArray(s.category_ids) && s.category_ids.some(id => subCatIdsInMain.includes(id))
            );
        }

        // 2. Filter by Verified Only
        if (verifiedOnly) {
            list = list.filter(s => s.is_verified || s.status === 'verified');
        }

        // 3. Filter by Experience
        if (selectedExp !== 'all') {
            list = list.filter(s => {
                const years = parseInt(s.experience, 10);
                if (isNaN(years)) return false;
                if (selectedExp === '1-3') return years >= 1 && years <= 3;
                if (selectedExp === '3-5') return years >= 3 && years <= 5;
                if (selectedExp === '5+') return years >= 5;
                return false;
            });
        }

        // 3b. Filters from Mobile Top Bar / FilterPanel (Service Type & Experience)
        if (filters.serviceType?.length) {
            const types = filters.serviceType.map(t => t.toLowerCase());
            list = list.filter(s => {
                const sNames = [
                    ...(Array.isArray(s.category_ids) ? s.category_ids.map(id => categoryMap[id]?.name || '') : []),
                    ...(Array.isArray(s.services) ? s.services : [s.services || '']),
                    s.service_detail || '',
                    s.product_list || '',
                    s.supplier_name || s.name || '',
                ].join(' ').toLowerCase();

                return types.some(t => sNames.includes(t));
            });
        }

        if (filters.experience?.length) {
            list = list.filter(s => {
                const years = parseInt(s.experience, 10);
                if (isNaN(years)) return false;
                return filters.experience.some(expOpt => {
                    if (expOpt === '1-3') return years >= 1 && years <= 3;
                    if (expOpt === '3-5') return years >= 3 && years <= 5;
                    if (expOpt === '5+') return years >= 5;
                    return false;
                });
            });
        }

        // 4. Filter by Search Keyword
        if (searchKeyword.trim()) {
            const query = searchKeyword.toLowerCase().trim();
            list = list.filter(s => {
                const name = (s.supplier_name || s.name || '').toLowerCase();
                const contact = (s.contact_name || '').toLowerCase();
                const detail = (s.service_detail || s.product_list || '').toLowerCase();
                const addr = (s.address || '').toLowerCase();
                const servicesStr = Array.isArray(s.services) ? s.services.join(' ').toLowerCase() : '';
                return (
                    name.includes(query) ||
                    contact.includes(query) ||
                    detail.includes(query) ||
                    addr.includes(query) ||
                    servicesStr.includes(query)
                );
            });
        }

        // 5. Sorting
        if (sortBy === 'views') {
            list.sort((a, b) => (b.views_count || 0) - (a.views_count || 0));
        } else if (sortBy === 'experience') {
            list.sort((a, b) => (parseInt(b.experience, 10) || 0) - (parseInt(a.experience, 10) || 0));
        } else if (sortBy === 'rating') {
            list.sort((a, b) => (parseFloat(b.rating) || 0) - (parseFloat(a.rating) || 0));
        } else if (sortBy === 'newest') {
            list.sort((a, b) => {
                const tA = a.created_at ? new Date(a.created_at).getTime() : 0;
                const tB = b.created_at ? new Date(b.created_at).getTime() : 0;
                return tB - tA;
            });
        }

        return list;
    }, [suppliers, selectedSubCatId, selectedMainCatId, subCategories, categoryMap, verifiedOnly, selectedExp, searchKeyword, sortBy, filters]);

    // Reset visible count when filters or suppliers change
    useEffect(() => {
        setVisibleCount(3);
        setIsLoadingMore(false);
    }, [suppliers, selectedSubCatId, selectedMainCatId, subCategories, verifiedOnly, selectedExp, searchKeyword, sortBy, filters]);

    const hasMore = visibleCount < filteredSuppliers.length;
    const visibleSuppliers = filteredSuppliers.slice(0, visibleCount);

    const stateRef = useRef({
        hasMore,
        loading,
        isFilterLoading,
        isLoadingMore,
    });
    useEffect(() => {
        stateRef.current = {
            hasMore,
            loading,
            isFilterLoading,
            isLoadingMore,
        };
    }, [hasMore, loading, isFilterLoading, isLoadingMore]);

    const handleLoadMore = useCallback(() => {
        if (stateRef.current.isLoadingMore || !stateRef.current.hasMore) return;
        setIsLoadingMore(true);
        stateRef.current.isLoadingMore = true;
        setTimeout(() => {
            setVisibleCount(prev => prev + 3);
            setIsLoadingMore(false);
            stateRef.current.isLoadingMore = false;
        }, 400);
    }, []);

    // Callback ref for bottom sentinel
    const setLoadMoreTriggerRef = useCallback((node) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        sentinelRef.current = node;
        if (!node) return;
        if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;

        const observer = new IntersectionObserver(
            (entries) => {
                const first = entries[0];
                if (first?.isIntersecting) {
                    const { hasMore, loading, isFilterLoading, isLoadingMore } = stateRef.current;
                    if (hasMore && !loading && !isFilterLoading && !isLoadingMore) {
                        handleLoadMore();
                    }
                }
            },
            {
                root: null,
                rootMargin: '250px',
                threshold: 0.05,
            }
        );
        observer.observe(node);
        observerRef.current = observer;
    }, [handleLoadMore]);

    // Cleanup observer on unmount
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    // Window scroll and resize fallback for reliable bottom detection across all mobile and desktop devices
    useEffect(() => {
        const handleScroll = () => {
            const { hasMore, loading, isFilterLoading, isLoadingMore } = stateRef.current;
            if (!hasMore || loading || isFilterLoading || isLoadingMore) return;

            const docEl = typeof document !== 'undefined' ? document.documentElement : null;
            const docBody = typeof document !== 'undefined' ? document.body : null;
            const scrollHeight = Math.max(docEl?.scrollHeight || 0, docBody?.scrollHeight || 0);
            const scrollTop = window.scrollY || window.pageYOffset || docEl?.scrollTop || docBody?.scrollTop || 0;
            const clientHeight = window.innerHeight || docEl?.clientHeight || 0;

                if (scrollHeight > 0 && scrollTop + clientHeight >= scrollHeight - 350) {
                    handleLoadMore();
                }
            };

            window.addEventListener('scroll', handleScroll, { passive: true });
            window.addEventListener('resize', handleScroll, { passive: true });
            return () => {
                window.removeEventListener('scroll', handleScroll);
                window.removeEventListener('resize', handleScroll);
            };
        }, [handleLoadMore]);

    const activeMainCategory = mainCategories.find(c => c.id === selectedMainCatId);
    const activeSubCategory = subCategories.find(sc => sc.id === selectedSubCatId);

    const handleOpenContact = (supplier) => {
        setActiveSupplierForContact(supplier);
        setShowInterestedPopup(true);
    };

    return (
        <>
            <Navbar />
            <MobileListingTopBar
                filters={filters}
                onFilterChange={(newFilters) => {
                    setFilters(newFilters);
                    triggerFilterTransition();
                }}
                listingType="civil"
                onSearch={(city, locs) => {
                    const params = new URLSearchParams();
                    if (city?.id) params.set('cityId', city.id);
                    if (locs?.length > 0) params.set('localities', locs.map(l => l.id).join('-'));
                    const targetCity = city?.name?.toLowerCase() || 'all';
                    router.push(`/civil/${targetCity}?${params.toString()}`);
                }}
            />

            <main className="min-h-screen bg-[#F8FAFC] pb-20">
                {/* 1. HIERARCHICAL CATEGORY HEADER (Tier 1: master_main_categories, Tier 2: master_product_categories) */}
                <CivilCategoryHeader
                    mainCategories={mainCategories}
                    subCategories={subCategories}
                    selectedMainCatId={selectedMainCatId}
                    onSelectMainCategory={handleSelectMainCategory}
                    selectedSubCatId={selectedSubCatId}
                    onSelectSubCategory={handleSelectSubCategory}
                    subCategoryCounts={subCategoryCounts}
                    mainCategoryCounts={mainCategoryCounts}
                    totalCount={suppliers.length}
                    searchKeyword={searchKeyword}
                    onSearchChange={setSearchKeyword}
                    cityName={cityName}
                />

                {/* Breadcrumb & Filter Bar */}
                <div className="bg-white border-b border-slate-200 sticky top-16 z-20 shadow-xs">
                    <div className="container-custom py-4 md:py-4.5 flex items-center justify-end md:justify-between gap-4">
                        {/* Left Breadcrumb & Current Context (Hidden on mobile) */}
                        <div className="hidden md:flex items-center gap-2 text-xs md:text-sm text-slate-500 overflow-x-auto whitespace-nowrap scrollbar-hide">
                            <Link href="/" className="hover:text-primary transition-colors font-medium">Home</Link>
                            <span>/</span>
                            <Link href="/civil" className="hover:text-primary transition-colors font-medium">Civil Marketplace</Link>
                            {activeMainCategory && (
                                <>
                                    <span>/</span>
                                    <button
                                        onClick={() => handleSelectMainCategory(activeMainCategory.id)}
                                        className="hover:text-primary transition-colors font-semibold text-slate-700"
                                    >
                                        {activeMainCategory.category_name}
                                    </button>
                                </>
                            )}
                            {activeSubCategory && (
                                <>
                                    <span>/</span>
                                    <span className="font-bold text-primary">{activeSubCategory.product_category_name}</span>
                                </>
                            )}
                            {cityName && (
                                <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full text-xs font-semibold ml-1">
                                    📍 {cityName}{localitiesNames ? ` (${localitiesNames})` : ''}
                                </span>
                            )}
                        </div>

                        {/* Right Sorting & Quick Filters */}
                        <div className="flex items-center gap-3 shrink-0 flex-wrap">
                            {/* Verified Toggle */}
                            {/* <label className="flex items-center gap-2 cursor-pointer bg-slate-50 hover:bg-slate-100 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 transition-colors select-none">
                                <input
                                    id="filter-verified-toggle"
                                    type="checkbox"
                                    checked={verifiedOnly}
                                    onChange={(e) => setVerifiedOnly(e.target.checked)}
                                    className="rounded text-primary focus:ring-primary h-3.5 w-3.5"
                                />
                                <span className="flex items-center gap-1">
                                    <span className="text-emerald-600">✓</span> Verified Only
                                </span>
                            </label> */}

                            {/* Experience Filter Dropdown */}
                            <div className="flex items-center gap-1 text-xs text-slate-600 font-medium">
                                <span>Exp:</span>
                                <select
                                    id="filter-experience"
                                    value={selectedExp}
                                    onChange={(e) => {
                                        setSelectedExp(e.target.value);
                                        triggerFilterTransition();
                                    }}
                                    className="h-8 text-xs px-1.5 rounded-lg bg-transparent text-slate-700 focus:outline-none font-bold cursor-pointer hover:text-primary transition-colors"
                                >
                                    <option value="all">Any Experience</option>
                                    <option value="1-3">1-3 Years</option>
                                    <option value="3-5">3-5 Years</option>
                                    <option value="5+">5+ Years</option>
                                </select>
                            </div>

                            {/* Sorting Dropdown */}
                            <div className="flex items-center gap-1 text-xs text-slate-600 font-medium">
                                <span>Sort:</span>
                                <select
                                    id="filter-sort-by"
                                    value={sortBy}
                                    onChange={(e) => {
                                        setSortBy(e.target.value);
                                        triggerFilterTransition();
                                    }}
                                    className="h-8 text-xs px-1.5 rounded-lg bg-transparent text-slate-700 focus:outline-none font-bold cursor-pointer hover:text-primary transition-colors"
                                >
                                    {sortOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content Section: Listings & Sidebar */}
                <div className="container-custom py-8">
                    {/* Header summary of current results */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4 mb-6 mt-3 md:mt-5">
                        <div>
                            <h2 className="text-base md:text-xl font-bold text-slate-800 flex items-center gap-2">
                                <span>
                                    {activeSubCategory
                                        ? `${activeSubCategory.product_category_name} Suppliers`
                                        : activeMainCategory
                                            ? `${activeMainCategory.category_name} Suppliers`
                                            : 'All Civil Suppliers & Services'}
                                </span>
                                {/* Desktop Count Badge */}
                                <span className="hidden md:inline-flex bg-primary/10 text-primary text-xs font-extrabold px-2.5 py-0.5 rounded-full">
                                    {loading || isFilterLoading ? 'Searching...' : `${filteredSuppliers.length} Found`}
                                </span>
                            </h2>
                            {activeSubCategory?.description && (
                                <p className="text-xs text-slate-500 mt-0.5 max-w-xl">
                                    {activeSubCategory.description}
                                </p>
                            )}
                        </div>

                        {/* Mobile 2nd Row: [X Found] on left & [Reset Filters] on right */}
                        <div className="flex items-center justify-between md:justify-end gap-3 w-full md:w-auto">
                            {/* Mobile Count Badge */}
                            <span className="md:hidden inline-flex bg-primary/10 text-primary text-xs font-extrabold px-2.5 py-0.5 rounded-full">
                                {loading || isFilterLoading ? 'Searching...' : `${filteredSuppliers.length} Found`}
                            </span>

                            {/* Reset filters button if filters are active */}
                            {(selectedMainCatId !== 'all' || selectedSubCatId !== 'all' || verifiedOnly || selectedExp !== 'all' || searchKeyword) && (
                                <button
                                    onClick={() => {
                                        handleSelectMainCategory('all');
                                        handleSelectSubCategory('all');
                                        setVerifiedOnly(false);
                                        setSelectedExp('all');
                                        setSearchKeyword('');
                                        triggerFilterTransition();
                                    }}
                                    className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
                                >
                                    <span>↺</span> Reset Filters
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                        {/* Left: Supplier Listings Grid (8 cols) */}
                        <div className="lg:col-span-8 flex flex-col gap-6">
                            {loading || isFilterLoading ? (
                                // Shimmer loading skeleton
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="bg-white rounded-2xl p-5 border border-slate-200 animate-pulse flex flex-col gap-3">
                                            <div className="h-6 bg-slate-200 rounded-md w-1/3"></div>
                                            <div className="flex gap-3">
                                                <div className="w-14 h-14 bg-slate-200 rounded-xl shrink-0"></div>
                                                <div className="flex-1 space-y-2">
                                                    <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                                                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                                                </div>
                                            </div>
                                            <div className="h-10 bg-slate-100 rounded-lg"></div>
                                            <div className="h-8 bg-slate-200 rounded-xl mt-2"></div>
                                        </div>
                                    ))}
                                </div>
                            ) : filteredSuppliers.length === 0 ? (
                                // Modern Empty State
                                <div className="bg-white rounded-3xl p-12 text-center border border-slate-200 shadow-sm flex flex-col items-center justify-center">
                                    <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center text-3xl mb-4">
                                        🔍
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-800 mb-1">
                                        No Suppliers Found
                                    </h3>
                                    <p className="text-sm text-slate-500 max-w-md mb-6 leading-relaxed">
                                        We couldn't find any suppliers matching your current selection
                                        {activeSubCategory ? ` in "${activeSubCategory.product_category_name}"` : ''}
                                        {cityName ? ` in ${cityName}` : ''}.
                                        Try selecting another category or resetting filters.
                                    </p>
                                    <div className="flex gap-3 flex-wrap justify-center">
                                        <button
                                            onClick={() => {
                                                handleSelectMainCategory('all');
                                                handleSelectSubCategory('all');
                                                setVerifiedOnly(false);
                                                setSelectedExp('all');
                                                setSearchKeyword('');
                                            }}
                                            className="btn btn-primary px-6 py-2.5 text-xs font-bold rounded-xl"
                                        >
                                            View All Suppliers
                                        </button>
                                        <Link
                                            href={isLoggedIn ? "/become-civil-suppliers" : "/register"}
                                            className="btn bg-slate-100 text-slate-700 hover:bg-slate-200 px-6 py-2.5 text-xs font-bold rounded-xl"
                                        >
                                            Register as a Supplier
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                // 3. SUPPLIER LISTINGS (Tier 3: suppliers table)
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4">
                                        {visibleSuppliers.map((supplier) => (
                                            <CivilSupplierCard
                                                key={supplier.id}
                                                supplier={supplier}
                                                categoryMap={categoryMap}
                                                searchParamsStr={searchParams.toString()}
                                                onContactClick={handleOpenContact}
                                            />
                                        ))}
                                    </div>

                                    {/* Bottom Sentinel & Infinite Scroll Loader */}
                                    {hasMore && (
                                        <div ref={setLoadMoreTriggerRef} className="py-4 flex flex-col items-center justify-center gap-3">
                                            {isLoadingMore ? (
                                                <>
                                                    <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div className="bg-white rounded-2xl p-5 border border-slate-200 animate-pulse flex flex-col gap-3">
                                                            <div className="h-6 bg-slate-200 rounded-md w-1/3"></div>
                                                            <div className="flex gap-3">
                                                                <div className="w-14 h-14 bg-slate-200 rounded-xl shrink-0"></div>
                                                                <div className="flex-1 space-y-2">
                                                                    <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                                                                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                                                                </div>
                                                            </div>
                                                            <div className="h-10 bg-slate-100 rounded-lg"></div>
                                                            <div className="h-8 bg-slate-200 rounded-xl mt-2"></div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white border border-slate-200/80 shadow-xs text-slate-600 text-xs font-medium">
                                                        <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></span>
                                                        <span>Loading more suppliers...</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="h-6 w-full flex items-center justify-center">
                                                    <span className="text-[11px] text-slate-400">Scroll down to load more</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {!hasMore && filteredSuppliers.length > 3 && (
                                        <div className="py-6 text-center">
                                            <p className="text-xs font-medium text-slate-400 flex items-center justify-center gap-1.5">
                                                <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                You've viewed all {filteredSuppliers.length} suppliers
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Right: Ad & Guidance Sidebar (4 cols) */}
                        <aside className="lg:col-span-4 flex flex-col gap-5 sticky top-36">
                            {/* Become a Supplier CTA Card */}
                            <ExpertHelpCard
                                title="Become a Civil Supplier"
                                description="Are you a contractor, material manufacturer, or civil engineer? Register your business to connect directly with thousands of verified clients."
                                buttonText="Register as Supplier"
                                link={isLoggedIn ? "/become-civil-suppliers" : "/register"}
                                bgClass="bg-gradient-to-br from-indigo-900 to-slate-900 text-white"
                                borderClass="border-indigo-800/60"
                                titleColorClass="text-white font-bold"
                                descColorClass="text-slate-300"
                                btnTextColorClass="text-slate-900 group-hover:text-primary font-bold"
                                glowColor1="#3b82f6"
                                glowColor2="#6366f1"
                            />

                            {/* Construction Advisory Card */}
                            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/80 rounded-2xl p-6 shadow-xs">
                                <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-800 flex items-center justify-center font-bold text-xl mb-3">
                                    🏗️
                                </div>
                                <h4 className="text-base font-bold text-amber-950 mb-1">
                                    Construction Material Guide
                                </h4>
                                <p className="text-xs text-amber-800/90 leading-relaxed mb-4">
                                    Compare bulk pricing for Cement, TMT Steel bars, Bricks, and River/M-Sand. Consult verified suppliers directly for prompt delivery and GST invoicing.
                                </p>
                                <div className="flex items-center gap-2 text-xs font-bold text-amber-900">
                                    <span>✓ Verified Vendors</span>
                                    <span>•</span>
                                    <span>✓ Direct Phone & WhatsApp</span>
                                </div>
                            </div>

                            {/* Ad Banners */}
                            <AdBanner
                                type="services"
                                noofads={2}
                            />
                        </aside>
                    </div>
                </div>
            </main>

            {/* Interested / Lead Generation Modal */}
            {activeSupplierForContact && (
                <InterestedPopup
                    isOpen={showInterestedPopup}
                    onClose={() => {
                        setShowInterestedPopup(false);
                        setActiveSupplierForContact(null);
                    }}
                    loggedInPerson={isLoggedIn}
                    listingId={activeSupplierForContact.id}
                    mode="service"
                    phone={activeSupplierForContact.mobile || activeSupplierForContact.phone}
                    agentName={activeSupplierForContact.supplier_name || activeSupplierForContact.name}
                    searchData=""
                />
            )}

            <Footer />
        </>
    );
}
